/**
 * Calendar Connector - OAuth2 Flow
 * Handles Google Calendar OAuth authentication and token exchange
 */

import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Token from localStorage
// Token from localStorage
const token = localStorage.getItem('token');
 
// Track if a token exchange is currently in progress to prevent duplicate calls during React re-renders
let isProcessingCalendarTokenExchange = false;

/**
 * Get Calendar credentials and create OAuth token
 * Initiates OAuth flow by redirecting to Google's consent screen
 */
export const getCalendarCredentialsAndCreateToken = async (ctdId, mcId, masterConnectors) => {
  try {
    console.log('🔑 Initiating Calendar OAuth flow...');
    console.log('Connector ID (ctdId):', ctdId);
    console.log('Master Connector ID (mcId):', mcId);

    // 1) Find Calendar master connector
    const calendarMasterConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() ||
        mc.mcId?.toString() === mcId?.toString()) &&
      mc.mcName === "Calendar"
    );

    if (!calendarMasterConnector) {
      throw new Error('Calendar master connector not found');
    }

    console.log('Calendar master connector found:', calendarMasterConnector);

    // 2) Fetch encrypted client_id and client_secret from backend
    const paramsRes = await fetch(buildConnectorParametersDecryptUrl(ctdId), {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!paramsRes.ok) {
      throw new Error('Failed to fetch connector parameters');
    }

    const connectorParams = await paramsRes.json();

    const clientId = connectorParams.find(p => p.scpName === 'clientid')?.scpValue;

    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    console.log('Client ID retrieved:', clientId.substring(0, 30) + '...');

    // 4) Store connector ID for post-redirect completion
    localStorage.setItem('PendingCalendarConnectorId', ctdId);
    localStorage.setItem('PendingCalendarMcId', mcId);

    // 5) Get scopes from master_connector (configurable, not hardcoded)
    // Fallback to default Calendar scopes if not configured
    let scopes = calendarMasterConnector.mcScope;
    if (!scopes || scopes.trim().length === 0) {
      scopes = 'https://www.googleapis.com/auth/calendar.events';
      console.warn('No scopes configured in master_connector, using default Calendar scope');
    }

    // 6) Build Google OAuth URL (frontend-side, like GoogleDrive)
    const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;

    // Use Master Connector auth URL
    const authEndpoint = calendarMasterConnector.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('Calendar Authentication URL is missing from Master Connector data');
    }

    const googleAuthUrl =
      `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scopes)}` +
      `&access_type=offline` +  // Request refresh token
      `&prompt=consent` +        // Force consent to ensure refresh token
      `&state=${encodeURIComponent(JSON.stringify({ ctdId, mcId }))}`;

    console.log('Redirecting to Google OAuth...');

    // 7) Redirect to Google OAuth (frontend-side redirect, no backend call)
    window.location.href = googleAuthUrl;

    // Return null to indicate we're redirecting (prevents status update)
    return null;

  } catch (error) {
    console.error('Error initiating Calendar OAuth:', error);
    throw error;
  }
};

/**
 * Exchange Calendar OAuth authorization code for access token
 * Called after user grants permission and returns from Google
 */
export const exchangeCalendarAuthCodeForToken = async (authCode, ctdId, mcId) => {
  // Prevent duplicate calls
  if (isProcessingCalendarTokenExchange) {
    console.warn('⚠️ Calendar token exchange is already in progress, skipping duplicate call');
    return null;
  }
 
  isProcessingCalendarTokenExchange = true;
 
  try {
    console.log('🔄 Exchanging Calendar auth code for token...');
    console.log('Auth Code:', authCode.substring(0, 20) + '...');
    console.log('Connector ID:', ctdId);
    console.log('Master Connector ID:', mcId);

    // Get JWT token from localStorage
    const jwtToken = localStorage.getItem('token');

    // Call backend to exchange auth code for tokens
    const response = await fetch(`${BACKEND_URL}synctrack/calendar/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'Authorization': `Bearer ${jwtToken}`
      },
      body: new URLSearchParams({
        code: authCode,
        mcId: mcId,
        ctdId: ctdId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Backend error response:', errorText);
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
    }

    const tokenData = await response.json();

    console.log('✅ Calendar token received successfully');
    console.log('Access Token:', tokenData.access_token ? `${tokenData.access_token.substring(0, 30)}...` : 'Not provided');
    console.log('Refresh Token:', tokenData.refresh_token ? `${tokenData.refresh_token.substring(0, 20)}...` : 'Not provided');
    console.log('Expires In:', tokenData.expires_in, 'seconds');

    return {
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token,
      expires_in: tokenData.expires_in || 3600,
      scope: tokenData.scope,
      token_type: tokenData.token_type || 'Bearer'
    };
 
  } catch (error) {
    console.error('Error exchanging Calendar auth code:', error);
    throw error;
  } finally {
    // Reset the processing flag
    isProcessingCalendarTokenExchange = false;
  }
};

/**
 * Refresh Calendar access token using refresh token
 */
export const refreshCalendarToken = async (refreshToken, mcId, ctdId) => {
  try {
    console.log('🔄 Refreshing Calendar token...');

    const jwtToken = localStorage.getItem('token');

    const response = await fetch(`${BACKEND_URL}synctrack/calendar/refreshToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'Authorization': `Bearer ${jwtToken}`
      },
      body: new URLSearchParams({
        refreshToken: refreshToken,
        mcId: mcId,
        ctdId: ctdId
      })
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const tokenData = await response.json();

    console.log('✅ Calendar token refreshed successfully');

    return {
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token || refreshToken,
      expires_in: tokenData.expires_in || 3600
    };

  } catch (error) {
    console.error('Error refreshing Calendar token:', error);
    throw error;
  }
};

